import { ICompanyPure, ICompanyTerm } from './../models/Company'
import {
  BuiltInTermConfigurations,
  IActionStep,
  TermActions,
} from './termConfigurations.helper'

export const getCompanyTermDuration = (
  company: ICompanyPure,
  termConfigName: string
): number => {
  const config = getCompanyTermConfiguration(company, termConfigName)
  if (config) {
    return (
      config.find((e) => e.action === TermActions.FINALISE_TERM)
        ?.monthsAfterStart || 6
    )
  }
  return 6
}

export const getStartAndEndDateOfTerm = (
  company: ICompanyPure,
  term: ICompanyTerm
): { termStart: Date | undefined; termEnd: Date | undefined } => {
  const start = term ? new Date(term.dateCreated) : undefined
  const end =
    term && company && start
      ? new Date(
          new Date(start).setMonth(
            start.getMonth() + getCompanyTermDuration(company, term.termPlan)
          )
        )
      : undefined

  return { termStart: start, termEnd: end }
}

export const getCompanyTermConfiguration = (
  company: ICompanyPure,
  termConfigName: string
): IActionStep[] | undefined => {
  if (termConfigName in (company.termsInformation.companyTerms || {})) {
    return company.termsInformation.companyTerms![termConfigName]
  }

  if (termConfigName in BuiltInTermConfigurations) {
    return BuiltInTermConfigurations[termConfigName]
  }
}

export const addStepTimeToDate = (date: Date, step: IActionStep): Date => {
  const newDate = new Date('getTime' in date ? date?.getTime() : Date.now())
  newDate.setMonth(newDate.getMonth() + step.monthsAfterStart)
  if (step.weeksAfterStart) {
    newDate.setDate(newDate.getDate() + 7 * step.weeksAfterStart)
  }
  if (step.specificDateInMonth) {
    newDate.setDate(step.specificDateInMonth)
  }
  return newDate
}

/**
 * The term start for which `step` falls on `date`: the inverse of addStepTimeToDate.
 * That one adds the months first and the weeks after, so the weeks come off first
 * here - the other way round a month boundary moves the step by a day. Months differ
 * in length, so an exact start does not always exist; the loop then settles on the
 * latest start whose step is not past `date`.
 */
export const subtractStepTimeFromDate = (
  date: Date,
  step: IActionStep
): Date => {
  const start = new Date(date)
  start.setDate(start.getDate() - 7 * (step.weeksAfterStart || 0))
  start.setMonth(start.getMonth() - step.monthsAfterStart)
  while (addStepTimeToDate(start, step) > date) {
    start.setDate(start.getDate() - 1)
  }
  return start
}

const getDateOfTermEvent = (
  company: ICompanyPure,
  termNumber: number,
  event: TermActions
): number | false => {
  const term = company.termsInformation.terms.find(
    (t) => t.termNumber === termNumber
  )

  const configuration = getCompanyTermConfiguration(
    company,
    term?.termPlan || company.termsInformation.selectedPlan
  )
  if (!configuration) {
    return false
  }

  const step = configuration.find((item) => item.action === event)
  if (!step) {
    return false
  }

  const startDate =
    getStartingDateOfTermNumber(company, termNumber) || new Date()
  const stepDate = addStepTimeToDate(startDate, step)

  return stepDate.getTime()
}

export const getStartingDateOfTermNumber = (
  company: ICompanyPure,
  termNumber: number
): Date | undefined => {
  // Calculate the right date and steps of the given term
  const companyTerms = company.termsInformation.terms
  if (termNumber <= company.state.currentTermNumber) {
    const correspondingTerm = company.termsInformation.terms.find(
      (term) => term.termNumber === termNumber
    )
    if (!correspondingTerm) {
      return
    }
    return new Date(correspondingTerm.dateCreated)
  }
  const currentTermDuration = getCompanyTermDuration(
    company,
    companyTerms[companyTerms.length - 1]?.termPlan
  )
  const currentTermEndDate = companyTerms.length
    ? new Date(companyTerms[companyTerms.length - 1].dateCreated)
    : undefined
  currentTermEndDate?.setMonth(
    currentTermEndDate?.getMonth() + currentTermDuration
  )
  for (let i = company.state.currentTermNumber; i < termNumber - 1; i++) {
    currentTermEndDate?.setMonth(
      currentTermEndDate?.getMonth() +
        getCompanyTermDuration(company, getCompanyPlanByTermNumber(company, i))
    )
  }
  return currentTermEndDate
}

const getCompanyPlanByTermNumber = (
  company: ICompanyPure,
  termNumber: number
): string => {
  // If the given term number has already passed, just fetch it from the company object
  if (termNumber <= company.state.currentTermNumber) {
    const correspondingTerm = company.termsInformation.terms.find(
      (term) => term.termNumber === termNumber
    )
    if (!correspondingTerm) {
      return company.termsInformation.selectedPlan
    }
    return correspondingTerm.termPlan
  }

  // If there is no configuration for looping plans,
  // the "selectedPlan" property is the company term plan for all future terms
  if (!company.termsInformation.plansLoop) {
    return company.termsInformation.selectedPlan
  }

  // If there is a defined loop, calculate which item should be the one given
  const selectedPlanIndex = company.termsInformation.plansLoop.indexOf(
    company.termsInformation.selectedPlan
  )

  // Calculate which plan is the one corresponding to the input parameter
  return selectedPlanIndex < 0
    ? company.termsInformation.selectedPlan
    : company.termsInformation.plansLoop[
        (selectedPlanIndex + (termNumber - company.state.currentTermNumber)) %
          company.termsInformation.plansLoop.length
      ]
}

const getExecutionDateOfTermActionOfCurrentTerm = (
  company: ICompanyPure,
  termAction: TermActions
): Date | undefined => {
  const stepsOfSelectedPlan = getCompanyTermConfiguration(
    company,
    company.termsInformation.selectedPlan
  )
  if (!stepsOfSelectedPlan) {
    return undefined
  }
  const selectedStep = stepsOfSelectedPlan.find(
    (step) => step.action === termAction
  )
  if (!selectedStep) {
    return undefined
  }
  const mostRecentTerm =
    company.termsInformation.terms[company.termsInformation.terms.length - 1]
  const date = new Date(mostRecentTerm.dateCreated)
  const dateOfExecution = addStepTimeToDate(date, selectedStep)
  return dateOfExecution
}

export default {
  getDateOfTermEvent,
  getCompanyPlanByTermNumber,
  getExecutionDateOfTermActionOfCurrentTerm,
}
